import type {
	ToastData,
	ToastDataWithoutIntent
} from '#lib/common/Presentation/Toast/ToastServiceInterface.js';
import type ToastServiceInterface from '#lib/common/Presentation/Toast/ToastServiceInterface.js';
import { type ButtonVariant } from '#lib/components/ui/button/index.js';
import { toast, type ExternalToast } from 'svelte-sonner';

export default class SonnerToast implements ToastServiceInterface {
	toast(data: ToastData): void {
		if (data.intent) {
			switch (data.intent) {
				case 'warning':
					this.warning(data);
					return;
				case 'success':
					this.success(data);
					return;
				case 'error':
					this.error(data);
					return;
			}
		}
		toast(data.title, this.#getOptions(data));
	}

	success(data: ToastDataWithoutIntent): void {
		toast.success(data.title, this.#getOptions(data));
	}

	error(data: ToastDataWithoutIntent): void {
		toast.error(data.title, this.#getOptions(data));
	}

	warning(data: ToastDataWithoutIntent): void {
		toast.warning(data.title, this.#getOptions(data));
	}

	#getOptions(data: ToastDataWithoutIntent): ExternalToast {
		return {
			description: data.description,
			duration: data.duration,
			icon: data.icon,
			classes: {
				actionButton: this.#getButtonActionVariant(data.action?.variant)
			},
			action: data.action
				? {
						label: data.action.label,
						onClick: data.action.onClick
					}
				: undefined
		};
	}

	// This method returns the appropriate CSS classes for the action button based on the provided variant. unfortunately we must use the important flag to override the default styles of sonner, because overwise we would have to restyle completely the toast as we can't just override the button styles.
	#getButtonActionVariant(variant: ButtonVariant = 'default'): string {
		switch (variant) {
			case 'destructive':
				return 'bg-destructive/10! hover:bg-destructive/20! focus-visible:ring-destructive/20! dark:focus-visible:ring-destructive/40! dark:bg-destructive/20! text-destructive! focus-visible:border-destructive/40! dark:hover:bg-destructive/30!';
			case 'outline':
				return 'border-border! border! bg-background! hover:bg-muted! text-foreground! hover:text-foreground! dark:bg-input/30! dark:border-input! dark:hover:bg-input/50! aria-expanded:bg-muted! aria-expanded:text-foreground! shadow-xs!';
			case 'secondary':
				return 'bg-secondary! text-secondary-foreground! hover:bg-secondary/80! aria-expanded:bg-secondary! aria-expanded:text-secondary-foreground!';
			case 'ghost':
				return 'hover:bg-muted! bg-transparent! text-foreground! hover:text-foreground! dark:hover:bg-muted/50! aria-expanded:bg-muted! aria-expanded:text-foreground!';
			case 'link':
				return 'text-primary! bg-transparent! underline-offset-4! hover:underline!';
			case 'default':
			default:
				return 'bg-primary! text-primary-foreground! hover:bg-primary/80!';
		}
	}
}
